'use client';

import { isBrowser } from 'utils';

/**
 * React hook for interacting with browser storage (localStorage or sessionStorage) in a type-safe way.
 *
 * Allows configuration of persistence via hook initialization.
 *
 * @template T - The type of value to store (default: string)
 * @param persistence - true for localStorage (persistent), false for sessionStorage (not persistent)
 * @returns Object with setStorageObject, getStorageObject, and removeStorageObject methods
 */
export function useBrowserStorage<T = string>(persistence = true) {
  const storage = isBrowser() ? (persistence ? window.localStorage : window.sessionStorage) : undefined;

  /**
   * Stores a value in storage under the given key.
   * If the value is a string, it is stored directly.
   * Otherwise, the value is stringified as JSON.
   *
   * @param id - The storage key
   * @param value - The value to store
   */
  const setStorageObject = (id: string, value: T) => {
    if (storage) {
      if (typeof value === 'string') {
        storage.setItem(id, value);
      } else {
        storage.setItem(id, JSON.stringify(value));
      }
    }
  };

  /**
   * Retrieves a value from storage by key.
   * If T is string, returns the value as-is.
   * Otherwise, parses the value as JSON.
   * Returns undefined if the key does not exist or parsing fails.
   *
   * @param id - The storage key
   * @returns The stored value (type T) or undefined
   */
  const getStorageObject = (id: string): T | undefined => {
    if (!storage) return undefined;

    try {
      const item = storage.getItem(id);

      if (item) {
        // If T is string, return directly; otherwise, parse as JSON
        try {
          return JSON.parse(item) as T;
        } catch {
          return item as T;
        }
      }
    } catch {
      return undefined;
    }
  };

  /**
   * Removes a value from storage by key.
   *
   * @param id - The storage key
   */
  const removeStorageObject = (id: string) => {
    if (storage) {
      storage.removeItem(id);
    }
  };

  return { setStorageObject, getStorageObject, removeStorageObject };
}
